/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IFilterSelectSearchProps, ISelectOptionItemType } from '../type';

const useFilterSearch = (props: IFilterSelectSearchProps) => {
  const {
    searchValue: propsSearchValue,
    onSearchValueChange,
    options,
    autoClearSearchValue,
    defaultSearchValue,
    isInit,
  } = props || {};
  const [internalSearchValue, setInternalSearchValue] = useState(defaultSearchValue || ''); // 非受控模式内部状态值
  const [isControlled, setIsControlled] = useState(false);

  useEffect(() => {
    if (isControlled || typeof propsSearchValue !== 'undefined') {
      setIsControlled(true);
    }
  }, [propsSearchValue]);

  // 搜索change事件
  const handleSearchValueChange = (searchValue: string, content?: any) => {
    // 初始状态会触发change，有初始值或保留值需做判断
    if (!content && (!autoClearSearchValue || !isInit)) {
      return;
    }
    if (!isControlled) {
      setInternalSearchValue(searchValue);
    }
    onSearchValueChange?.(searchValue);
  };

  // 此函数主要用于在字符串中查找和替换特殊字符，用于处理可能会引发正则表达式错误的字符串
  const searchValueEscape = (searchValue: string) => {
    const specialCharacter = ['\\', '$', '(', ')', '*', '+', '.', '[', '?', '^', '{', '|',];
    specialCharacter.forEach((str) => {
      const reg = new RegExp(`\\${str}`, 'gim');
      searchValue = searchValue?.replace?.(reg, `\\${str}`);
    });
    return searchValue;
  };

  // 搜索高亮
  const replaceMatch = useCallback(
    (
      text: string,
      _searchValue = isControlled
        ? propsSearchValue || ''
        : internalSearchValue,
    ) => {
      _searchValue = searchValueEscape(_searchValue);
      const reg = new RegExp(`${_searchValue}`, 'gi');
      const setText = (text: any) =>
        `<span style='color: #006EFF'>${text}</span>`;
      return text?.replace?.(reg, (matchText: any) => setText(matchText));
    },
    [propsSearchValue, isControlled, internalSearchValue],
  );

  // 根据搜索结果渲染的options函数
  const matchOptions = useCallback(
    (list: Array<ISelectOptionItemType>) => {
      const matchOptionList: Array<ISelectOptionItemType> = [];
      list?.forEach((optionItem: ISelectOptionItemType) => {
        const filterText = typeof optionItem?.text === 'string'
          ? replaceMatch(optionItem?.text)
          : optionItem?.text;
        // 过滤掉不包含 searchValue 的项
        if (optionItem?.text === filterText) return;
        matchOptionList?.push({
          ...optionItem,
          text: filterText,
        });
      });
      return matchOptionList;
    },
    [replaceMatch],
  );

  // 根据搜索结果渲染的options radioOptions
  const { renderOptions } = useMemo(() => {
    // 无 searchValue 展示全部
    if (isControlled ? !propsSearchValue : !internalSearchValue)
      return { renderOptions: options };
    const renderOptions: Array<ISelectOptionItemType> = matchOptions(options);
    return { renderOptions };
  }, [
    matchOptions,
    options,
    propsSearchValue,
    internalSearchValue,
    isControlled,
  ]);

  return {
    searchValue: isControlled ? propsSearchValue : internalSearchValue,
    renderOptions,
    // setSearchValue,
    replaceMatch,
    handleSearchValueChange,
  };
};

export default useFilterSearch;
